import { useEffect, useState, useCallback, useRef } from "react";
import { getPartners, createPartner, updatePartner, deletePartner, reorderPartners, IPartner } from "../../api/partners";
import { Plus, Pencil, Trash, ArrowUp, ArrowDown } from "lucide-react";
import { storage } from "../../services/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./styles.scss";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import {
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Field,
  IconButton,
  Input,
  Loader,
  PageHeader,
  TableWrap,
  UploadBox,
} from "../../ui";

interface PartnerFormData {
  name: string;
  logoUrl: string;
}

const defaultFormData: PartnerFormData = { name: "", logoUrl: "" };

const Partners = () => {
  const dispatch = useAppDispatch();
  const [partners, setPartners] = useState<IPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reordering, setReordering] = useState(false);

  // Dialog state
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<IPartner | null>(null);
  const [partnerToDelete, setPartnerToDelete] = useState<IPartner | null>(null);
  const [formData, setFormData] = useState<PartnerFormData>(defaultFormData);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await getPartners();
      const items = res?.data || [];
      setPartners(Array.isArray(items) ? items : []);
    } catch (err: any) {
      console.error("Error fetching partners", err);
      setPartners([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Partners";
    fetchPartners();
  }, [fetchPartners]);

  const closeForm = () => {
    if (saving || uploadingLogo) return;
    setIsFormDialogOpen(false);
    setEditingPartner(null);
  };

  const handleAdd = () => {
    setEditingPartner(null);
    setFormData(defaultFormData);
    setIsFormDialogOpen(true);
  };

  const handleEdit = (partner: IPartner) => {
    setEditingPartner(partner);
    setFormData({ name: partner.name, logoUrl: partner.logoUrl });
    setIsFormDialogOpen(true);
  };

  const handleLogoUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      dispatch(setGeneralAlertMessage({ type: "error", message: "Please select an image file" }));
      return;
    }
    setUploadingLogo(true);
    try {
      const fileName = `partners/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "")}`;
      const snapshot = await uploadBytes(ref(storage, fileName), file);
      const url = await getDownloadURL(snapshot.ref);
      setFormData((prev) => ({ ...prev, logoUrl: url }));
    } catch (err: any) {
      console.error("Upload error:", err);
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to upload logo" }));
    } finally {
      setUploadingLogo(false);
    }
  };

  const canSave = formData.name.trim() !== "" && formData.logoUrl !== "";

  const handleSave = async () => {
    if (!canSave) return;

    setSaving(true);
    try {
      const payload = { name: formData.name.trim(), logoUrl: formData.logoUrl };
      if (editingPartner) {
        await updatePartner(editingPartner.id, payload);
        dispatch(setGeneralAlertMessage({ message: "Partner updated successfully", type: "success" }));
      } else {
        await createPartner(payload);
        dispatch(setGeneralAlertMessage({ message: "Partner added successfully", type: "success" }));
      }
      setIsFormDialogOpen(false);
      setEditingPartner(null);
      await fetchPartners();
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ message: err?.message || "Failed to save partner", type: "error" }));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!partnerToDelete) return;

    setDeleting(true);
    try {
      await deletePartner(partnerToDelete.id);
      dispatch(setGeneralAlertMessage({ message: "Partner deleted successfully", type: "success" }));
      setPartnerToDelete(null);
      await fetchPartners();
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ message: err?.message || "Failed to delete partner", type: "error" }));
    } finally {
      setDeleting(false);
    }
  };

  // Swap with the neighbour, show it straight away, and put it back if the save fails.
  const handleMove = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (reordering || target < 0 || target >= partners.length) return;

    const previous = partners;
    const next = [...partners];
    [next[index], next[target]] = [next[target], next[index]];
    setPartners(next);
    setReordering(true);
    try {
      await reorderPartners(next.map((p) => p.id));
    } catch (err: any) {
      setPartners(previous);
      dispatch(setGeneralAlertMessage({ message: err?.message || "Failed to reorder partners", type: "error" }));
    } finally {
      setReordering(false);
    }
  };

  // Keep the dialogs' words steady while they animate closed
  const formTitle = useRef("Add Partner");
  const formSaveLabel = useRef("Add");
  if (isFormDialogOpen) {
    formTitle.current = editingPartner ? "Edit Partner" : "Add Partner";
    formSaveLabel.current = editingPartner ? "Update" : "Add";
  }
  const lastPartnerToDelete = useRef<IPartner | null>(null);
  if (partnerToDelete) lastPartnerToDelete.current = partnerToDelete;

  return (
    <div className="partners-page">
      <PageHeader
        title="Partners"
        subtitle={`${partners.length} ${partners.length === 1 ? "Partner" : "Partners"} · shown in this order on the website and app home pages`}
        actions={
          <Button icon={<Plus size={18} />} onClick={handleAdd}>
            Add Partner
          </Button>
        }
      />

      {loading && partners.length === 0 ? (
        <Loader />
      ) : partners.length === 0 ? (
        <Card padding="none">
          <EmptyState title="No partners yet." description="Partners you add will appear on the website and app." />
        </Card>
      ) : (
        <>
          <TableWrap className="partners-table-wrap">
            <table className="ui-table partners-table">
              <thead>
                <tr>
                  <th>Logo</th>
                  <th>Name</th>
                  <th>Order</th>
                  <th className="ui-table__actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((partner, index) => (
                  <tr key={partner.id}>
                    <td>
                      <div className="partner-logo">
                        <img src={partner.logoUrl} alt={partner.name} />
                      </div>
                    </td>
                    <td className="partner-name">{partner.name}</td>
                    <td>
                      <div className="order-buttons">
                        <IconButton
                          size="sm"
                          label="Move up"
                          icon={<ArrowUp size={16} />}
                          onClick={() => handleMove(index, -1)}
                          disabled={reordering || index === 0}
                        />
                        <IconButton
                          size="sm"
                          label="Move down"
                          icon={<ArrowDown size={16} />}
                          onClick={() => handleMove(index, 1)}
                          disabled={reordering || index === partners.length - 1}
                        />
                      </div>
                    </td>
                    <td className="ui-table__actions">
                      <IconButton label="Edit" icon={<Pencil size={16} />} onClick={() => handleEdit(partner)} />
                      <IconButton
                        label="Delete"
                        variant="danger"
                        icon={<Trash size={16} />}
                        onClick={() => setPartnerToDelete(partner)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>

          {/* Phones: the same rows as cards (the table above is hidden under 768px) */}
          <div className="partner-cards">
            {partners.map((partner, index) => (
              <Card key={partner.id} padding="sm" className="partner-card">
                <div className="partner-card__main">
                  <div className="partner-logo">
                    <img src={partner.logoUrl} alt={partner.name} />
                  </div>
                  <p className="partner-card__name">{partner.name}</p>
                </div>
                <div className="partner-card__actions">
                  <IconButton size="sm" label="Edit" icon={<Pencil size={16} />} onClick={() => handleEdit(partner)} />
                  <IconButton
                    size="sm"
                    label="Delete"
                    variant="danger"
                    icon={<Trash size={16} />}
                    onClick={() => setPartnerToDelete(partner)}
                  />
                </div>
                <div className="partner-card__foot">
                  <span className="partner-card__order-label">Order</span>
                  <div className="order-buttons">
                    <IconButton
                      size="sm"
                      label="Move up"
                      icon={<ArrowUp size={16} />}
                      onClick={() => handleMove(index, -1)}
                      disabled={reordering || index === 0}
                    />
                    <IconButton
                      size="sm"
                      label="Move down"
                      icon={<ArrowDown size={16} />}
                      onClick={() => handleMove(index, 1)}
                      disabled={reordering || index === partners.length - 1}
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={isFormDialogOpen}
        onClose={closeForm}
        title={formTitle.current}
        busy={saving || uploadingLogo}
        bodyClassName="partner-form"
        footer={
          <>
            <Button variant="ghost" onClick={closeForm} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving} disabled={uploadingLogo || !canSave}>
              {formSaveLabel.current}
            </Button>
          </>
        }
      >
        <Field label="Name" required>
          {(fieldId) => (
            <Input
              id={fieldId}
              type="text"
              value={formData.name}
              maxLength={200}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Enter partner name"
            />
          )}
        </Field>
        <Field label="Logo" required>
          <UploadBox
            className="partner-logo-upload"
            previewUrl={formData.logoUrl || null}
            onPick={handleLogoUpload}
            onRemove={() => setFormData((prev) => ({ ...prev, logoUrl: "" }))}
            uploading={uploadingLogo}
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            label="Click to upload logo"
            hint="PNG or SVG with a transparent background works best"
          />
        </Field>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!partnerToDelete}
        onClose={(confirmed) => {
          // A successful delete clears it itself; a failed one stays open, as before
          if (!confirmed) setPartnerToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        loading={deleting}
        title="Delete Partner"
        message={
          <>
            Are you sure you want to delete "<strong>{lastPartnerToDelete.current?.name}</strong>"? It will no longer
            appear on the website or app.
          </>
        }
        confirmLabel="Delete"
      />
    </div>
  );
};

export default Partners;
